import { cx } from "@linaria/core";
import React, { BaseHTMLAttributes, FC, forwardRef, memo, useEffect, useMemo, useRef } from "react";
import { useRouter } from "next/router";
import { BreadcrumbsPropsType } from "@/components/Breadcrumbs/types";
import { useWindowSize } from "@/hooks/useWindowSize";
import { getBreakpointVal } from "@/styles/utils/Utils";
import { breakpoints } from "@/styles/utils/vars";
import { NavItemType } from "@/types";
import { Button } from "@/ui/Button/Button";
import { SITE_URL } from "@/utils/constants";
import BreadcrumbItem from "./BreadcrumbItem";
import { cssBreadcrumbCurrent, cssBreadcrumbNavLink, cssBreadcrumbsScrollEnded, cssBreadcrumbsScrollNotStarted, StyledBreadcrumbs, StyledBreadcrumbsInner } from "./StyledBreadcrumbs";
export const Breadcrumbs: FC<BreadcrumbsPropsType> = ({
  breadcrumbs
}) => {
  const router = useRouter();
  const {
    width
  } = useWindowSize();
  const breadcrumbsInnerRef = useRef<HTMLDivElement>(null);
  const isLessMd = width !== undefined && width <= getBreakpointVal(breakpoints.md);
  const breadcrumbsItems: (NavItemType & {
    uuid: string;
  })[] = useMemo(() => Object.entries(breadcrumbs || {}).map(([uuid, {
    path,
    title = "",
    subItems
  }], i, arr) => {
    // для сео нужно чтоб последний пункт хлебных крошек не был ссылкой,
    // которая равна текущей странице
    const isLink = router.asPath.includes("?") ? true : router.asPath.includes(path) ? !(arr.length === i + 1) : true;
    return {
      uuid,
      path,
      title,
      subItems: isLessMd ? [] : subItems,
      isLink
    };
  }), [breadcrumbs, isLessMd, router.asPath]);
  useEffect(() => {
    let breadcrumbsInnerEl = breadcrumbsInnerRef.current;
    const handleScroll = () => {
      const breadcrumbsInnerEl = breadcrumbsInnerRef.current;
      if (!!breadcrumbsInnerEl) {
        if (breadcrumbsInnerEl.parentElement) {
          if (breadcrumbsInnerEl.scrollLeft > 0) {
            breadcrumbsInnerEl.parentElement.classList.remove(cssBreadcrumbsScrollNotStarted);
          } else {
            breadcrumbsInnerEl.parentElement.classList.add(cssBreadcrumbsScrollNotStarted);
          }
          if (!Math.floor(Math.ceil(breadcrumbsInnerEl.scrollWidth - breadcrumbsInnerEl.parentElement?.scrollWidth) - breadcrumbsInnerEl.scrollLeft)) {
            breadcrumbsInnerEl.parentElement.classList.add(cssBreadcrumbsScrollEnded);
          } else {
            breadcrumbsInnerEl.parentElement.classList.remove(cssBreadcrumbsScrollEnded);
          }
        }
      }
    };
    if (!!breadcrumbsInnerEl) {
      breadcrumbsInnerEl.addEventListener("scroll", handleScroll, {
        passive: true
      });
      handleScroll();
      return () => {
        breadcrumbsInnerEl?.removeEventListener("scroll", handleScroll);
      };
    } else {
      const t = setTimeout(() => {
        breadcrumbsInnerEl = breadcrumbsInnerRef.current;
        handleScroll();
        breadcrumbsInnerEl?.addEventListener("scroll", handleScroll, {
          passive: true
        });
      }, 0);
      return () => {
        clearTimeout(t);
        breadcrumbsInnerEl?.removeEventListener("scroll", handleScroll);
      };
    }
  }, [breadcrumbsInnerRef.current]);
  if (!breadcrumbsItems.length) {
    return <></>;
  }
  return <BreadcrumbsList items={breadcrumbsItems} ref={breadcrumbsInnerRef}>
      <Button onClick={() => router.back()} type={"button"} variant={"translucent"} icon={"ArrowLeft"} size={"small"} className={cx(cssBreadcrumbNavLink)} isClean isHiddenBg>
        Назад
      </Button>
    </BreadcrumbsList>;
};
const BreadcrumbsList = memo(forwardRef<HTMLDivElement, BaseHTMLAttributes<HTMLDivElement> & {
  items: (NavItemType & {
    uuid: string;
  })[];
}>(({
  items,
  children
}, ref) => {
  return <>
        <StyledBreadcrumbs>
          <StyledBreadcrumbsInner ref={ref}>
            {items.map(({
          uuid,
          ...b
        }, index) => <BreadcrumbItem key={uuid} {...b} id={uuid} className={cx(index === items.length - 1 && cssBreadcrumbCurrent)} />)}
          </StyledBreadcrumbsInner>

          {children}
        </StyledBreadcrumbs>
        <script dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, index) => {
          const {
            title,
            path
          } = item;
          return {
            "@type": "ListItem",
            position: index + 1,
            name: String(title),
            item: `${SITE_URL}${path}`
          };
        })
      })
    }} type="application/ld+json" />
      </>;
}));
Breadcrumbs.displayName = "Breadcrumbs";
BreadcrumbsList.displayName = "BreadcrumbsList";